import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, Lock } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Two modes in the same page:
//   - With `?token=...` query param   → show the "set new password" form
//   - Without a token                 → show the "request reset link" form
const ResetPasswordPage = () => {
  const { language } = useAuth();
  const L = (en, de) => (language === 'de' ? de : en);
  const [sp] = useSearchParams();
  const token = sp.get('token');
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [done, setDone] = useState(false);

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/request`, {
        email: email.trim().toLowerCase(),
        origin_url: window.location.origin,
      });
      setSent(true);
    } catch (_) {
      // Intentionally silent — same response whether or not account exists.
      setSent(true);
    } finally { setLoading(false); }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error(L('Password must be at least 6 characters', 'Passwort muss mindestens 6 Zeichen haben'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(L("Passwords don't match", 'Passwörter stimmen nicht überein'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, { token, new_password: newPassword });
      setDone(true);
      toast.success(L('Password reset — you can log in now', 'Passwort zurückgesetzt — du kannst dich jetzt anmelden'));
      setTimeout(() => navigate('/login', { replace: true }), 2200);
    } catch (err) {
      toast.error(err.response?.data?.detail || L('Invalid or expired token', 'Ungültiger oder abgelaufener Token'));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-slate-50 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white rounded-2xl border border-slate-100 shadow-xl shadow-slate-900/5 p-8"
        data-testid="reset-password-card"
      >
        <div className="mb-6 text-center">
          <div className="inline-flex w-14 h-14 rounded-full bg-emerald-50 items-center justify-center mb-3">
            <Lock size={24} className="text-emerald-600" weight="duotone" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">
            {token ? L('Set a new password', 'Neues Passwort setzen') : L('Forgot your password?', 'Passwort vergessen?')}
          </h1>
          <p className="text-sm text-slate-500 mt-1 leading-relaxed">
            {token
              ? L('Enter a new password for your account.', 'Gib ein neues Passwort für dein Konto ein.')
              : L("We'll email you a link to reset it.", 'Wir senden dir einen Link zum Zurücksetzen per E-Mail.')}
          </p>
        </div>

        {!token && !sent && (
          <form onSubmit={handleRequest} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-semibold">{L('Email', 'E-Mail')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="bg-slate-50 border-slate-200"
                data-testid="reset-email-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold h-11" data-testid="reset-request-submit">
              {loading ? '…' : L('Send reset link', 'Link senden')}
            </Button>
            <div className="text-center text-sm pt-2">
              <Link to="/login" className="text-slate-500 hover:text-emerald-600">{L('Back to login', 'Zurück zur Anmeldung')}</Link>
            </div>
          </form>
        )}

        {!token && sent && (
          <div className="text-center space-y-4" data-testid="reset-sent-screen">
            <CheckCircle size={56} className="text-emerald-500 mx-auto" weight="fill" />
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-1">{L('Check your inbox', 'E-Mail-Posteingang prüfen')}</h2>
              <p className="text-sm text-slate-500 leading-relaxed">
                {L("If an account with that email exists, we've sent a reset link that's valid for 1 hour.",
                   'Falls ein Konto mit dieser E-Mail existiert, haben wir einen Reset-Link gesendet (1 Stunde gültig).')}
              </p>
            </div>
            <Link to="/login" className="inline-block text-sm text-emerald-600 hover:text-emerald-700 font-semibold pt-2">
              {L('Back to login', 'Zurück zur Anmeldung')}
            </Link>
          </div>
        )}

        {token && !done && (
          <form onSubmit={handleConfirm} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="newPassword" className="text-sm font-semibold">{L('New password', 'Neues Passwort')}</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="bg-slate-50 border-slate-200"
                data-testid="reset-new-password-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword" className="text-sm font-semibold">{L('Confirm password', 'Passwort bestätigen')}</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="bg-slate-50 border-slate-200"
                data-testid="reset-confirm-password-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold h-11" data-testid="reset-confirm-submit">
              {loading ? '…' : L('Update password', 'Passwort aktualisieren')}
            </Button>
          </form>
        )}

        {token && done && (
          <div className="text-center space-y-4" data-testid="reset-done-screen">
            <CheckCircle size={56} className="text-emerald-500 mx-auto" weight="fill" />
            <h2 className="text-lg font-bold text-slate-900">{L('Password updated', 'Passwort aktualisiert')}</h2>
            <p className="text-sm text-slate-500">{L('Redirecting to login…', 'Weiterleitung zur Anmeldung…')}</p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
